// Fix prettier/eslint conflicts for the opening brace after export class...
/* eslint-disable brace-style */
import { Component, OnDestroy, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
  DialogService,
} from '@next/core-lib/dialog';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, first, Observable, Subject, takeUntil } from 'rxjs';

import { filter, map, startWith } from 'rxjs/operators';

import { Admission, CropAid } from '../../../../crop-aid/crop-aid.model';
import * as CropAidSelectors from '../../../../crop-aid/store/crop-aid.selectors';
import { SalesOrderPeriod } from '../../../../sales-order-period/models/sales-order-period.models';
import * as SalesOrderPeriodSelectors from '../../../../sales-order-period/store/sales-order-period.selectors';
import { AppState } from '../../../../shared/models';
import { SewageSludgeCompostTransport } from '../../../models/sewage-sludge-compost-transport.model';

export interface SewageSludgeCompostEditTransportData extends AbstractDialogData {
  transport: SewageSludgeCompostTransport;
}

interface SewageSludgeCompostEditTransportFormGroup {
  cropAidId: FormControl;
  dosage: FormControl;
  totalNitrogen: FormControl;
  totalPhosphate: FormControl;
  periodId: FormControl;
  comment: FormControl;
}

@Component({
  selector: 'app-sewage-sludge-compost-edit-transport-dialog',
  templateUrl: './sewage-sludge-compost-edit-transport.component.html',
  styleUrls: ['./sewage-sludge-compost-edit-transport.component.scss'],
  standalone: false,
})
export class SewageSludgeCompostEditTransportComponent
  extends AbstractDialog<SewageSludgeCompostEditTransportData>
  implements OnDestroy
{
  data = inject<SewageSludgeCompostEditTransportData>(MAT_DIALOG_DATA);
  private readonly store = inject<Store<AppState>>(Store);
  protected readonly mdDialogRef =
    inject<
      MatDialogRef<
        AbstractDialog<SewageSludgeCompostEditTransportData>,
        AbstractDialogResultData<SewageSludgeCompostEditTransportData>
      >
    >(MatDialogRef);
  dialog = inject(DialogService);

  formGroup: FormGroup = new FormGroup({});
  protected readonly first = first;
  private readonly destroyed$ = new Subject<boolean>();
  protected readonly salesOrderPeriods$: Observable<SalesOrderPeriod[]> = this.store.pipe(
    select(SalesOrderPeriodSelectors.selectSalesOrderPeriods),
    takeUntil(this.destroyed$),
  );
  private cropAids = new BehaviorSubject<CropAid[]>([]);
  protected filteredCropAids$: Observable<CropAid[]> = this.cropAids.pipe(map((cs) => cs));
  private readonly salesOrderPeriods = new BehaviorSubject<SalesOrderPeriod[]>([]);

  constructor() {
    super() /* istanbul ignore next */;
    const data = this.data;

    this.store.select(CropAidSelectors.selectCropAidFertilizers).subscribe((cs) => {
      this.cropAids.next(cs);
    });
    this.salesOrderPeriods$.subscribe((periods) => this.salesOrderPeriods.next(periods));

    const { transport } = data;
    this.formGroup = new FormGroup<SewageSludgeCompostEditTransportFormGroup>({
      cropAidId: new FormControl(transport.cropAid?.id, [
        Validators.required,
        this.cropAidValidator(),
      ]),
      dosage: new FormControl(transport.tonnage, [Validators.required]),
      totalNitrogen: new FormControl(transport.nitrogenInKg ?? 0, [Validators.required]),
      totalPhosphate: new FormControl(transport.phosphateInKg ?? 0, [Validators.required]),
      periodId: new FormControl(transport.periodId),
      comment: new FormControl(transport.comment),
    });

    this.filteredCropAids$ = combineLatest([
      this.formGroup.get('cropAidId').valueChanges.pipe(
        startWith(''),
        filter((value) => typeof value === 'string'),
        takeUntil(this.destroyed$),
      ),
      this.cropAids,
    ]).pipe(
      map(([value, cropAids]) => this.filterProducts(value, cropAids)),
      takeUntil(this.destroyed$),
    );
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  protected closeDialog() {
    this.mdDialogRef.close();
  }

  protected confirmTransportEdit() {
    if (this.formGroup.valid) {
      const cropAid = this.findProduct(this.formGroup.value.cropAidId);

      const transport: SewageSludgeCompostTransport = {
        ...this.data.transport,
        cropAid: {
          id: cropAid?.id,
          name: cropAid?.name,
          unitOfMeasurement: cropAid?.unitOfMeasurement,
        },
        tonnage: this.formGroup.value.dosage,
        nitrogenInKg: this.formGroup.value.totalNitrogen,
        phosphateInKg: this.formGroup.value.totalPhosphate,
        periodId: this.formGroup.value.periodId,
        comment: this.formGroup.value.comment,
      };

      this.confirm({ transport });
    }
  }

  protected displayProduct() {
    return (cropAidId?: number) => (cropAidId ? this.findProduct(cropAidId)?.name : '');
  }

  protected getAdmissionNumberString(admissions: Admission[]) {
    if (!admissions || admissions.length < 1) {
      return;
    }

    return `(${admissions.map((admission) => admission.number).join(', ')})`;
  }

  private findProduct(cropAidId: number): CropAid {
    return this.cropAids.value.find((cropAid) => cropAid.id === cropAidId);
  }

  private filterProducts(value: string, cropAids: CropAid[]): CropAid[] {
    const filterValue = value.toLowerCase();
    return cropAids
      .filter((option) => option.isActive)
      .filter((option) => option.name.toLowerCase().includes(filterValue));
  }

  private cropAidValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return {
          required: { value: control.value },
        };
      }

      if (typeof control.value === 'string') {
        return {
          selection: { value: control.value },
        };
      }

      return null;
    };
  }
}
